import { z } from 'zod';

/**
 * Stable, machine-readable error codes returned by the Rynora API.
 *
 * NEVER rename a code — the frontend keys off `code` to look up display
 * copy in `@rynora/messages`. Renames are breaking changes for every
 * client (browser apps, SDK, CLI, third-party integrators).
 *
 * Categories are flat (no enum nesting) for easier grep + i18n keys.
 * Prefix convention: <DOMAIN>_<SHORT_CAUSE>.
 */
export const RynoraErrorCode = {
  // ---- Generic ----
  UNKNOWN: 'UNKNOWN',
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  NOT_FOUND: 'NOT_FOUND',
  BAD_REQUEST: 'BAD_REQUEST',
  RATE_LIMITED: 'RATE_LIMITED',

  // ---- Validation ----
  VALIDATION_INVALID_BODY: 'VALIDATION_INVALID_BODY',
  VALIDATION_REQUIRED_FIELD: 'VALIDATION_REQUIRED_FIELD',
  VALIDATION_INVALID_FORMAT: 'VALIDATION_INVALID_FORMAT',

  // ---- Auth ----
  AUTH_REQUIRED: 'AUTH_REQUIRED',
  AUTH_INVALID_CREDENTIALS: 'AUTH_INVALID_CREDENTIALS',
  AUTH_EMAIL_NOT_FOUND: 'AUTH_EMAIL_NOT_FOUND',
  AUTH_EMAIL_ALREADY_EXISTS: 'AUTH_EMAIL_ALREADY_EXISTS',
  AUTH_INVALID_TOKEN: 'AUTH_INVALID_TOKEN',
  AUTH_INVALID_API_KEY: 'AUTH_INVALID_API_KEY',
  AUTH_INSUFFICIENT_SCOPE: 'AUTH_INSUFFICIENT_SCOPE',
  /**
   * The CI / API key was rotated, the 24 h overlap window has
   * passed, and the nightly cron sweep marked it expired. Clients
   * should swap to the new key issued at rotation time.
   */
  CI_KEY_EXPIRED: 'CI_KEY_EXPIRED',
  /**
   * Operator clicked Rotate on a key that already has a deprecated_at
   * set — the previous rotation hasn't finished its overlap window.
   * The frontend can either show the existing rotation lineage or
   * wait for the cron to expire the old key.
   */
  CI_KEY_ALREADY_DEPRECATED: 'CI_KEY_ALREADY_DEPRECATED',
  AUTH_RESET_TOKEN_EXPIRED: 'AUTH_RESET_TOKEN_EXPIRED',
  AUTH_PROVIDER_DISABLED: 'AUTH_PROVIDER_DISABLED',
  /** Email is not on the env's allowlist (registrationMode='allowlisted'). */
  AUTH_NOT_ALLOWLISTED: 'AUTH_NOT_ALLOWLISTED',
  /** registrationMode='closed' — nobody can register, log-in still allowed. */
  AUTH_REGISTRATION_CLOSED: 'AUTH_REGISTRATION_CLOSED',

  // ---- MFA ----
  MFA_CODE_INVALID: 'MFA_CODE_INVALID',
  MFA_SESSION_EXPIRED: 'MFA_SESSION_EXPIRED',
  MFA_NOT_ENROLLED: 'MFA_NOT_ENROLLED',

  // ---- Billing ----
  BILLING_NOT_CONFIGURED: 'BILLING_NOT_CONFIGURED',
  BILLING_PLAN_LIMIT_EXCEEDED: 'BILLING_PLAN_LIMIT_EXCEEDED',
  BILLING_INVALID_PLAN: 'BILLING_INVALID_PLAN',

  // ---- Payments / multi-gateway (migration 074) ----
  /**
   * The active payment provider id is in the schema enum but not in
   * `IMPLEMENTED_PAYMENT_PROVIDERS` — i.e. the operator selected a
   * provider whose adapter has not been wired up yet (Paddle/PayPal/
   * Lemon Squeezy ship as schema-forward stubs).
   */
  PAYMENT_PROVIDER_NOT_IMPLEMENTED: 'PAYMENT_PROVIDER_NOT_IMPLEMENTED',
  /**
   * The active provider's credentials (key + webhook secret) are
   * missing from `admin_config.payments.<provider>` and from the
   * legacy env-var fallback. Returned as HTTP 503 so the caller's UI
   * can prompt the operator to paste keys in Admin Settings.
   */
  PAYMENT_PROVIDER_NOT_CONFIGURED: 'PAYMENT_PROVIDER_NOT_CONFIGURED',
  /**
   * Webhook handler rejected an inbound event because the
   * provider-specific signature header failed verification (HMAC
   * mismatch, expired timestamp, replayed body, etc.).
   */
  PAYMENT_WEBHOOK_INVALID_SIGNATURE: 'PAYMENT_WEBHOOK_INVALID_SIGNATURE',
  /**
   * Caller asked to checkout with a provider whose currency does not
   * match the org's billing region (e.g. Razorpay INR for an org with
   * `billing_country='US'`). Surfaces with an "Choose a different
   * provider" CTA.
   */
  PAYMENT_REGION_MISMATCH: 'PAYMENT_REGION_MISMATCH',

  // ---- Projects ----
  /**
   * The requested project either doesn't exist, has been hard-deleted,
   * or belongs to a different org. We return the same code for all three
   * so project ids aren't an oracle for org membership.
   */
  PROJECT_NOT_FOUND: 'PROJECT_NOT_FOUND',
  /**
   * Caller tried to create or rename a project to a name that is already
   * in use by another active (non-soft-deleted) project in the same org.
   * The check is case-insensitive (matching the partial unique index at
   * migrations/<NNN>_projects_unique_name.sql) so `"Acme"` collides with
   * `"acme"`. Names of soft-deleted projects are reusable — once a row
   * lands in trash its name is freed.
   */
  PROJECT_NAME_TAKEN: 'PROJECT_NAME_TAKEN',
  /**
   * Caller submitted a create/rename request with an empty / whitespace
   * project name. The frontend should catch this before submit, but the
   * server enforces it independently so the contract holds for any
   * client (CLI, direct API, third-party integrations).
   */
  PROJECT_NAME_REQUIRED: 'PROJECT_NAME_REQUIRED',

  // ---- Flows / runs ----
  FLOW_NOT_FOUND: 'FLOW_NOT_FOUND',
  FLOW_NAME_TAKEN: 'FLOW_NAME_TAKEN',
  FLOW_NAME_REQUIRED: 'FLOW_NAME_REQUIRED',
  FLOW_VERSION_NOT_FOUND: 'FLOW_VERSION_NOT_FOUND',
  RUN_NOT_FOUND: 'RUN_NOT_FOUND',
  RUN_NO_TRACE: 'RUN_NO_TRACE',
  RUN_INVALID_STATE: 'RUN_INVALID_STATE',
  /**
   * The trace registry row (Migration 081) for the requested trace id
   * either doesn't exist or belongs to a different org. We return the
   * same code for both so trace ids aren't an oracle for org membership.
   */
  TRACE_NOT_FOUND: 'TRACE_NOT_FOUND',
  /**
   * Tracing has been globally disabled by an operator via
   * `runner.traceEnabled = false` in admin-config. Existing trace
   * rows stay queryable for audit but the download endpoint refuses
   * to serve the bytes until the kill switch is flipped back on.
   */
  TRACE_DISABLED: 'TRACE_DISABLED',
  /**
   * The trace registry row exists and the kill switch is on, but the
   * on-disk file is missing / unreadable / fell outside the
   * sandboxed `runner.traceDir`. Distinct from TRACE_NOT_FOUND so the
   * UI can surface a "this run's trace was swept by retention or moved"
   * empty state instead of a generic 500.
   */
  TRACE_STREAMING_FAILED: 'TRACE_STREAMING_FAILED',

  // ---- Flow review + collaborative editing (migration 046) ----
  /**
   * Two clients tried to save the same flow. Either the lease is held
   * by another user or the supplied `If-Match: <draftVersion>` is
   * stale. The frontend should refetch + show a "Sue saved a newer
   * version, refresh to see it" toast.
   */
  FLOW_LEASE_HELD_BY_OTHER: 'FLOW_LEASE_HELD_BY_OTHER',
  /**
   * The PUT body's draft_version doesn't match the row's current
   * draft_version. Lease check passed but a parallel write slipped in
   * (race between lease takeover and save). Same UX as
   * FLOW_LEASE_HELD_BY_OTHER: refetch + toast.
   */
  FLOW_DRAFT_VERSION_STALE: 'FLOW_DRAFT_VERSION_STALE',
  /**
   * `POST /flows/:id/publish` was rejected because the project's
   * review_policy.requires_review = true but no approved review
   * exists for the current draft_version. Frontend should redirect
   * the user to "Request review".
   */
  FLOW_REVIEW_REQUIRED: 'FLOW_REVIEW_REQUIRED',
  /**
   * A pending review exists but the approvals_count is below the
   * project's `review_policy.min_approvers`. Wait for more approvers
   * before publishing.
   */
  FLOW_REVIEW_INSUFFICIENT_APPROVALS: 'FLOW_REVIEW_INSUFFICIENT_APPROVALS',
  /**
   * The author tried to approve their own review but the project's
   * `review_policy.allow_self_approve` is false. Surfaces on the
   * "Approve" button click for reviews where requested_by = req.user.id.
   */
  FLOW_REVIEW_SELF_APPROVE_DISABLED: 'FLOW_REVIEW_SELF_APPROVE_DISABLED',

  // ---- SCM-style flow review (migration 047 onward) ----
  /** GET / PATCH / merge of a branch that doesn't exist (or is in another org). */
  FLOW_BRANCH_NOT_FOUND: 'FLOW_BRANCH_NOT_FOUND',
  /**
   * The branch and `main` both touched the same step id since the
   * branch's `base_version`. v1 doesn't auto-resolve — author has to
   * fork a fresh branch off the new published version.
   */
  FLOW_BRANCH_CONFLICT: 'FLOW_BRANCH_CONFLICT',
  /**
   * Tried to take a review action (decide / re-request) as a reviewer
   * who was removed from the review's reviewer list.
   */
  FLOW_REVIEW_REVIEWER_REMOVED: 'FLOW_REVIEW_REVIEWER_REMOVED',
  /**
   * Tried to merge a branch but at least one required status check
   * is not in the `passed` state.
   */
  FLOW_REVIEW_CHECKS_FAILED: 'FLOW_REVIEW_CHECKS_FAILED',
  /**
   * Tried to merge a branch but at least one required reviewer has
   * not yet approved (or has open `changes_requested`).
   */
  FLOW_REVIEW_REQUIRED_REVIEWERS_MISSING: 'FLOW_REVIEW_REQUIRED_REVIEWERS_MISSING',

  // ---- Org / membership ----
  ORG_NOT_FOUND: 'ORG_NOT_FOUND',
  ORG_FORBIDDEN: 'ORG_FORBIDDEN',
  ORG_INVITE_INVALID: 'ORG_INVITE_INVALID',
  ORG_MEMBER_NOT_FOUND: 'ORG_MEMBER_NOT_FOUND',
  ORG_LAST_OWNER: 'ORG_LAST_OWNER',
  ORG_NAME_REQUIRED: 'ORG_NAME_REQUIRED',
  /** Returned to a caller without owner role attempting to remove the org's original creator. */
  ORG_CANNOT_REMOVE_OWNER: 'ORG_CANNOT_REMOVE_OWNER',
  /**
   * Mig 148 — the active org has flipped on the org-level AI opt-out
   * toggle. Every LLM-backed surface (`/api/intent/*`,
   * `/api/scripts/generate`, etc.) refuses with this code so the
   * frontend can render a precise "AI features disabled by your org
   * admin" notice instead of a generic 403. The toggle is configured
   * at `/dashboard/security` and audit-logged as
   * ORG_AI_OPT_OUT_CHANGED.
   */
  ORG_AI_DISABLED: 'ORG_AI_DISABLED',

  /**
   * The org admin tried to register an audit-log SIEM forwarding
   * endpoint pointing at a URL the server refused to accept. Today
   * we only forward to HTTPS, public-internet hostnames — `http://`,
   * RFC1918, link-local, loopback and `*.svc.cluster.local` are
   * refused so a misconfigured customer can't turn the forwarder
   * into an SSRF pivot.
   */
  AUDIT_SIEM_URL_REJECTED: 'AUDIT_SIEM_URL_REJECTED',
  /**
   * The audit-siem worker auto-disabled an endpoint after the
   * consecutive-failure threshold was crossed. The frontend uses
   * this code to render the "endpoint disabled — fix and re-enable"
   * affordance with the recorded `lastFailureReason`.
   */
  AUDIT_SIEM_ENDPOINT_AUTO_DISABLED: 'AUDIT_SIEM_ENDPOINT_AUTO_DISABLED',

  // ---- DSAR self-service portal (Batch 2 PR #5 / mig 151) ----
  /**
   * The DSAR ticket reference posted to GET /api/public/dsar/:ref
   * didn't match any row. Renders a generic "not found" on the
   * status page — we deliberately don't leak whether the reference
   * exists but belongs to someone else.
   */
  DSAR_TICKET_NOT_FOUND: 'DSAR_TICKET_NOT_FOUND',
  /**
   * Public submission was throttled. The portal applies a per-IP +
   * per-email rate limit (5 / 15 min) to stop spam-clicking the form
   * from filling the queue.
   */
  DSAR_RATE_LIMITED: 'DSAR_RATE_LIMITED',
  /**
   * Admin PATCH attempted an illegal status transition (e.g.
   * `fulfilled` → `received`). The lifecycle is forward-only with
   * terminal `fulfilled` / `rejected` / `cancelled` states.
   */
  DSAR_INVALID_TRANSITION: 'DSAR_INVALID_TRANSITION',
  /**
   * Admin tried to extend a row beyond the GDPR Art. 12(3) ceiling
   * (original 30 days + up to 60 day extension = 90 days total).
   */
  DSAR_EXTENSION_LIMIT: 'DSAR_EXTENSION_LIMIT',

  // ---- Customer-managed encryption keys (CMK / BYOK — Batch 3 PR / mig 152) ----
  /**
   * A read of an encrypted column under the BYOK path failed because
   * the org's `org_encryption_keys` row is in `disabled` state past
   * its grace window and the wrapped DEK has been crypto-shredded —
   * the rows can no longer be decrypted by anybody. Returned as HTTP
   * 503 with `Retry-After` cleared (the situation is terminal, not
   * transient). The route layer surfaces this on the admin UI as
   * "key shredded — re-enrol restores no data."
   */
  ORG_CMK_UNAVAILABLE: 'ORG_CMK_UNAVAILABLE',
  /**
   * Caller tried to rotate / disable / fetch state but the org has
   * never enrolled. Distinct from `ORG_CMK_UNAVAILABLE` so the admin
   * UI can render "you are using platform keys" instead of "your key
   * has been shredded".
   */
  ORG_CMK_NOT_ENROLLED: 'ORG_CMK_NOT_ENROLLED',
  /**
   * The base64 KEK the customer pasted into the enrol / rotate form
   * did not decode to exactly 32 bytes. Surface a clear "your key
   * must be base64-encoded and 256 bits" message; do not leak which
   * specific check failed (length vs base64 decode).
   */
  ORG_CMK_INVALID_KEY: 'ORG_CMK_INVALID_KEY',
  /**
   * Enrolment refused because one of the documented pre-requisites
   * was not satisfied. v1 enforces:
   *   - the org has flipped on the `require_mfa_for_password_auth`
   *     mandate (PR #51); AND
   *   - at least one `audit_siem_endpoints` row exists for the org
   *     with `enabled = TRUE` (PR #53).
   * The route returns the missing prereqs in `detail.missing[]` so
   * the admin UI can deep-link to each one.
   */
  ORG_CMK_PREREQ_FAILED: 'ORG_CMK_PREREQ_FAILED',
  /**
   * POST /encryption/enroll on an org that already has an active or
   * rotating row. The caller should `PATCH /encryption/rotate`
   * instead, or `POST /encryption/disable` then re-enrol.
   */
  ORG_CMK_ALREADY_ENROLLED: 'ORG_CMK_ALREADY_ENROLLED',
  /**
   * POST /encryption/disable typed-confirmation phrase did not match
   * the exact required string. The route refuses the disable so a
   * misclick can't crypto-shred a customer's tenant secrets.
   */
  ORG_CMK_CONFIRMATION_MISMATCH: 'ORG_CMK_CONFIRMATION_MISMATCH',

  // ---- Platform admin (lock-in roadmap Stage A0) ----
  /** X-Rynora-Act-As-Org used by a non-platform_admin caller. */
  PLATFORM_ADMIN_REQUIRED: 'PLATFORM_ADMIN_REQUIRED',
  /**
   * The user has `users.platform_role='platform_admin'` in the DB but the
   * `RYNORA_PLATFORM_ADMIN_ENABLED` bootstrap flag is unset in the running
   * process. The role is treated as absent at runtime; this code is
   * surfaced only when the user explicitly tries to use a platform_admin-only
   * endpoint (act-as, /admin/*).
   */
  PLATFORM_ADMIN_DISABLED: 'PLATFORM_ADMIN_DISABLED',

  // ---- Reports / artefacts ----
  REPORT_NOT_FOUND: 'REPORT_NOT_FOUND',
  REPORT_ASSET_PATH_REQUIRED: 'REPORT_ASSET_PATH_REQUIRED',

  // ---- Support / contact ----
  SUPPORT_MESSAGE_REQUIRED: 'SUPPORT_MESSAGE_REQUIRED',
  SUPPORT_DELIVERY_FAILED: 'SUPPORT_DELIVERY_FAILED',

  // ---- Admin config ----
  ADMIN_CONFIG_INVALID_SECTION: 'ADMIN_CONFIG_INVALID_SECTION',
  ADMIN_CONFIG_INVALID_VALUE: 'ADMIN_CONFIG_INVALID_VALUE',
  ADMIN_CONFIG_PERSIST_FAILED: 'ADMIN_CONFIG_PERSIST_FAILED',

  // ---- SSO ----
  SSO_INVALID_METADATA: 'SSO_INVALID_METADATA',
  SSO_DOMAIN_TAKEN: 'SSO_DOMAIN_TAKEN',
  SSO_NOT_CONFIGURED: 'SSO_NOT_CONFIGURED',

  // ---- SCIM provisioning (migration 058) ----
  /** Bearer token missing / unrecognised / revoked. SCIM clients
   *  should re-fetch from the IdP-side credential store. */
  SCIM_AUTH_INVALID: 'SCIM_AUTH_INVALID',
  /** SCIM resource id was not found in this org. */
  SCIM_RESOURCE_NOT_FOUND: 'SCIM_RESOURCE_NOT_FOUND',
  /** Operator tried to provision a user whose email is already a
   *  member of a *different* org via SSO. */
  SCIM_USER_CONFLICT: 'SCIM_USER_CONFLICT',
  /** PATCH op references a path that's not in the supported subset
   *  (RFC 7644 §3.5.2 — we support `replace`, `add`, `remove` on the
   *  flat top-level attributes plus `members` on Group). */
  SCIM_PATCH_UNSUPPORTED: 'SCIM_PATCH_UNSUPPORTED',
  /** A required SCIM Core attribute was missing — typically `userName`
   *  on POST /Users or `displayName` on POST /Groups. */
  SCIM_REQUIRED_ATTRIBUTE_MISSING: 'SCIM_REQUIRED_ATTRIBUTE_MISSING',

  // ---- Audit search (delivery roadmap Tier 2.1) ----
  /** Free-text FTS query is malformed (operator chars unbalanced, etc.). */
  AUDIT_QUERY_INVALID: 'AUDIT_QUERY_INVALID',
  /** Pagination cursor is missing, malformed, or signed by a different
   *  server instance. UI should restart from page 1. */
  AUDIT_CURSOR_INVALID: 'AUDIT_CURSOR_INVALID',
  /** CSV export hit the per-export row cap; tighten the filter. */
  AUDIT_EXPORT_TOO_LARGE: 'AUDIT_EXPORT_TOO_LARGE',

  // ---- Run retention (delivery roadmap Tier 2.2) ----
  /** Caller's plan caps retention below the requested value. */
  RUN_RETENTION_PLAN_CAP_EXCEEDED: 'RUN_RETENTION_PLAN_CAP_EXCEEDED',

  // ---- Data sources (delivery roadmap Tier 2.4) ----
  /** CSV payload exceeded the per-source byte cap. */
  DATA_SOURCE_TOO_LARGE: 'DATA_SOURCE_TOO_LARGE',
  /** CSV had no header row, or the header row was inconsistent
   *  with the row body (column count mismatch). */
  DATA_SOURCE_INVALID_CSV: 'DATA_SOURCE_INVALID_CSV',
  /** A data source with the same `name` already exists for the
   *  same `(org, project)`. PUT semantics: caller chose POST. */
  DATA_SOURCE_NAME_TAKEN: 'DATA_SOURCE_NAME_TAKEN',
  /** PUT was rejected because the supplied schema doesn't match the
   *  CSV header row. */
  DATA_SOURCE_SCHEMA_MISMATCH: 'DATA_SOURCE_SCHEMA_MISMATCH',

  // ---- Mobile emulation tier (delivery roadmap Tier 2.7) ----
  /** Caller picked a device id that's not in the bundled registry. */
  MOBILE_DEVICE_UNKNOWN: 'MOBILE_DEVICE_UNKNOWN',
  /** Caller picked a network preset id outside the {wifi, 4g, 3g-slow, offline} set. */
  MOBILE_NETWORK_UNKNOWN: 'MOBILE_NETWORK_UNKNOWN',
  /**
   * The runner aborted a navigation step because the requested
   * preset was `offline` and the step requires the network. Surfaces
   * via the run report's per-step error.
   */
  NETWORK_OFFLINE: 'NETWORK_OFFLINE',

  // ---- Parallel cross-browser fan-out (delivery roadmap Tier 2.6) ----
  /** Caller passed fewer than 2 capabilities to /api/runs/parallel. */
  PARALLEL_NEEDS_TWO_CAPABILITIES: 'PARALLEL_NEEDS_TWO_CAPABILITIES',
  /** Plan's `parallelWorkers` quota would be exceeded by the requested fan-out. */
  PARALLEL_PLAN_LIMIT_EXCEEDED: 'PARALLEL_PLAN_LIMIT_EXCEEDED',
  /** Fan-out parent run id was not found or belongs to another org. */
  PARALLEL_PARENT_NOT_FOUND: 'PARALLEL_PARENT_NOT_FOUND',

  // ---- Visual baseline review (delivery roadmap Tier 2.3) ----
  /** Promote target step is no longer a `visual-check` (the flow was
   *  edited after the run). UI should refresh the run view. */
  STEP_TYPE_MISMATCH: 'STEP_TYPE_MISMATCH',
  /** Promote target step has no recorded baseline / actual artifact
   *  on the run (e.g. the run was purged by retention). */
  VISUAL_DIFF_NOT_FOUND: 'VISUAL_DIFF_NOT_FOUND',
  /** Caller doesn't have `editor`+ on the org. Visual baseline
   *  promotions require the same role as flow edits. */
  VISUAL_BASELINE_FORBIDDEN: 'VISUAL_BASELINE_FORBIDDEN',

  // ---- pending-applitools-visual-ai v1.2 — cross-browser matrix ----
  /**
   * Submitter tried to start a visual-matrix run but
   * `admin-config.visualAi.matrix.enabled` is off. Returned 403
   * with a stable wire code so the FE can suggest the right
   * admin tab.
   */
  VISUAL_MATRIX_DISABLED: 'VISUAL_MATRIX_DISABLED',
  /**
   * Request `cells.length` exceeds the per-org cap
   * (`admin-config.visualAi.matrix.maxCells`, default 8, cap 20).
   * Body includes the configured limit + the submitted count.
   */
  VISUAL_MATRIX_TOO_MANY_CELLS: 'VISUAL_MATRIX_TOO_MANY_CELLS',
  /**
   * `anchorCellLabel` was set on the request but no cell in
   * `cells[]` had a matching label. Returned 422 — the FE should
   * surface a "no anchor selected" inline error on the picker.
   */
  VISUAL_MATRIX_ANCHOR_NOT_FOUND: 'VISUAL_MATRIX_ANCHOR_NOT_FOUND',

  // ---- Localization matrix (`pending-localization-smoke` v0.1) ----
  /**
   * Admin-config kill switch `localization.enabled` is `false`.
   * Operator must turn it on before any localization-matrix
   * submission lands. Returned as HTTP 403.
   */
  LOCALIZATION_MATRIX_DISABLED: 'LOCALIZATION_MATRIX_DISABLED',
  /**
   * Request `cells.length` exceeds the per-org cap
   * (`localization.maxLocales`, defaults to 24 — the locked-
   * decision §4 cap). Returned as HTTP 400.
   */
  LOCALIZATION_MATRIX_TOO_MANY_LOCALES: 'LOCALIZATION_MATRIX_TOO_MANY_LOCALES',
  /**
   * `anchorLocale` was set on the request but no cell in
   * `cells` has a matching locale tag. Returned as HTTP 400.
   */
  LOCALIZATION_MATRIX_ANCHOR_NOT_FOUND: 'LOCALIZATION_MATRIX_ANCHOR_NOT_FOUND',
  /**
   * One or more locale tags in `cells` were not present in the
   * canonical `LOCALE_REGISTRY` (the 13-locale list shipped in
   * `pending-localization-smoke` v0). Returned as HTTP 400 with
   * the unknown tags echoed in the response body.
   */
  LOCALIZATION_MATRIX_UNKNOWN_LOCALES: 'LOCALIZATION_MATRIX_UNKNOWN_LOCALES',

  // ---- Design-aware visual checks (`pending-design-aware-visual-checks` v0.1) ----
  /**
   * Admin-config kill switch `designAware.enabled` is `false`. The
   * operator must turn it on before any design-pin route accepts a
   * write. Returned as HTTP 403.
   */
  DESIGN_AWARE_DISABLED: 'DESIGN_AWARE_DISABLED',
  /**
   * No Figma PAT configured in `designAware.figmaToken` — the
   * resolver can't call Figma's REST API. Returned as HTTP 412
   * (precondition failed) because the operator can fix this in
   * Admin Settings.
   */
  DESIGN_FRAME_TOKEN_MISSING: 'DESIGN_FRAME_TOKEN_MISSING',
  /**
   * The pasted Figma share URL didn't pass `validateFigmaFrameUrl`.
   * Echoes the parser's diagnostic code in the response body so the
   * SPA can map it to a localised toast. Returned as HTTP 400.
   */
  DESIGN_FRAME_URL_INVALID: 'DESIGN_FRAME_URL_INVALID',
  /**
   * The Figma REST API call failed (network / 401 / 403 / 404 / 5xx).
   * The HTTP status from Figma is echoed in `meta.figmaStatus` so the
   * operator can disambiguate "wrong token" (401) from "frame deleted"
   * (404). Returned as HTTP 502 (bad-gateway upstream).
   */
  DESIGN_FRAME_RESOLVER_FAILED: 'DESIGN_FRAME_RESOLVER_FAILED',
  /**
   * Figma returned 200 but the `{ images: { <nodeId>: <url> } }`
   * response was missing the node we asked for — usually means the
   * frame was deleted but the file still exists. Returned as HTTP 404
   * so the operator updates the pin.
   */
  DESIGN_FRAME_NOT_FOUND: 'DESIGN_FRAME_NOT_FOUND',

  // ---- Design-aware visual checks v0.2 (multi-provider) ----
  /**
   * The pasted URL doesn't match any provider (Figma / Sketch / Penpot)
   * URL shape. Returned as HTTP 400. Surface this to the SPA's "paste a
   * design URL" modal so the operator knows we can't tell which tool
   * the link belongs to.
   */
  DESIGN_FRAME_PROVIDER_UNKNOWN: 'DESIGN_FRAME_PROVIDER_UNKNOWN',
  /**
   * Admin-config has no PAT for the provider the URL resolves to.
   * `meta.provider` echoes the missing-token provider so the SPA can
   * deep-link the operator to the right Admin Settings field.
   * Returned as HTTP 412.
   */
  DESIGN_PROVIDER_TOKEN_MISSING: 'DESIGN_PROVIDER_TOKEN_MISSING',

  // ---- Privacy / compliance scanner (v0.1) ----
  /**
   * `privacy.enabled === false` on admin-config. Returned as HTTP 403
   * from per-run + weekly-report routes so operators see a deliberate
   * "feature off" surface instead of empty arrays.
   */
  PRIVACY_SCAN_DISABLED: 'PRIVACY_SCAN_DISABLED',
  /**
   * The `?week=YYYY-Www` parameter on the weekly report route failed
   * validation — bad format, year out of band, or week outside `01-53`.
   * Returned as HTTP 400.
   */
  PRIVACY_REPORT_WINDOW_INVALID: 'PRIVACY_REPORT_WINDOW_INVALID',
  /**
   * A custom matcher pattern submitted to admin-config failed to
   * compile as a JavaScript regex (PATCH /api/admin/config rejects
   * the write). Returned as HTTP 400. `meta.id` echoes the offending
   * matcher id.
   */
  PRIVACY_CUSTOM_MATCHER_INVALID: 'PRIVACY_CUSTOM_MATCHER_INVALID',

  // ---- Notifications ----
  NOTIFICATION_PREFS_INVALID: 'NOTIFICATION_PREFS_INVALID',
  /** Type isn't registered in notification_type_config or the bootstrap map. */
  NOTIFICATION_TYPE_UNKNOWN: 'NOTIFICATION_TYPE_UNKNOWN',
  /** notifications.enabled = false (admin kill switch) or per-type enabled = false. */
  NOTIFICATION_DISABLED: 'NOTIFICATION_DISABLED',
  /** Unsubscribe token failed HMAC verification or is expired. */
  NOTIFICATION_UNSUBSCRIBE_INVALID: 'NOTIFICATION_UNSUBSCRIBE_INVALID',
  /** Webhook URL host is not in `allowedHostSuffixes` admin allow-list. */
  NOTIFICATION_CHANNEL_HOST_BLOCKED: 'NOTIFICATION_CHANNEL_HOST_BLOCKED',
  /** Marketing engine is disabled platform-wide; cannot send marketing campaigns. */
  NOTIFICATION_MARKETING_DISABLED: 'NOTIFICATION_MARKETING_DISABLED',

  // ---- Integrations ----
  INTEGRATION_NOT_CONFIGURED: 'INTEGRATION_NOT_CONFIGURED',
  INTEGRATION_DISCONNECTED: 'INTEGRATION_DISCONNECTED',
  INTEGRATION_FAILED: 'INTEGRATION_FAILED',

  // ---- API testing / suites ----
  API_TEST_NOT_FOUND: 'API_TEST_NOT_FOUND',
  API_TEST_INVALID_REQUEST: 'API_TEST_INVALID_REQUEST',
  SUITE_NOT_FOUND: 'SUITE_NOT_FOUND',

  // ---- Deployments ----
  DEPLOY_ENV_INVALID: 'DEPLOY_ENV_INVALID',
  DEPLOY_GITHUB_NOT_CONFIGURED: 'DEPLOY_GITHUB_NOT_CONFIGURED',
  DEPLOY_GITHUB_AUTH_FAILED: 'DEPLOY_GITHUB_AUTH_FAILED',
  DEPLOY_DISPATCH_FAILED: 'DEPLOY_DISPATCH_FAILED',
  DEPLOY_RUN_NOT_FOUND: 'DEPLOY_RUN_NOT_FOUND',
  DEPLOY_HEALTH_UNREACHABLE: 'DEPLOY_HEALTH_UNREACHABLE',
  DEPLOY_VERIFY_DISPATCH_FAILED: 'DEPLOY_VERIFY_DISPATCH_FAILED',
  DEPLOY_VERIFY_WORKFLOW_NOT_FOUND: 'DEPLOY_VERIFY_WORKFLOW_NOT_FOUND',
  DEPLOY_ROLLBACK_NO_CANDIDATE: 'DEPLOY_ROLLBACK_NO_CANDIDATE',
  DEPLOY_ROLLBACK_DISPATCH_FAILED: 'DEPLOY_ROLLBACK_DISPATCH_FAILED',

  // ---- Custom script step ----
  SCRIPT_RUNTIME_UNAVAILABLE: 'SCRIPT_RUNTIME_UNAVAILABLE',
  SCRIPT_TIMEOUT: 'SCRIPT_TIMEOUT',
  SCRIPT_RUNTIME_ERROR: 'SCRIPT_RUNTIME_ERROR',
  SCRIPT_OUTPUT_TYPE_MISMATCH: 'SCRIPT_OUTPUT_TYPE_MISMATCH',
  SCRIPT_OUTPUT_MISSING: 'SCRIPT_OUTPUT_MISSING',

  // ---- Inline `runJS()` browser-context step (pending-runjs-step) ----
  /**
   * The `runJS` step kind is gated by the master admin-config kill
   * switch (`noCode.runJs.enabled`, default OFF on fresh installs).
   * Either the editor disables the tile when the SPA's public config
   * reports this off, or the API refuses to save a step body. Operators
   * flip the kill switch on once they've reviewed the security posture.
   */
  RUNJS_DISABLED: 'RUNJS_DISABLED',
  /**
   * The code body referenced one of the banned patterns
   * (`eval(` / `new Function(` / `Function(`) — the coarse defence in
   * front of the browser sandbox. The real defence is the sandbox;
   * this catches the lazy attack and signals organisational intent.
   * Returned 422 with the matched pattern listed in the envelope.
   */
  RUNJS_BANNED_PATTERN: 'RUNJS_BANNED_PATTERN',
  /**
   * The `Promise.race` against the timeout fired before the user's
   * code resolved. The browser-side eval may continue running until
   * the next page navigation; the runner treats this step as failed
   * and proceeds.
   */
  RUNJS_TIMEOUT: 'RUNJS_TIMEOUT',
  /**
   * The user's code threw a non-`rynora.fail` error. Captured stack
   * frames are normalised through the existing `extractTopStackFrames`
   * helper so the test-observability tier picks up `runJS` failures
   * with the same fingerprint pipeline as every other step.
   */
  RUNJS_RUNTIME_ERROR: 'RUNJS_RUNTIME_ERROR',
  /**
   * An emitted output's runtime type did not match the declared
   * `ScriptOutput.type`. Mirrors `SCRIPT_OUTPUT_TYPE_MISMATCH` —
   * the wire shape is shared, the error code is split so the
   * run-report can tell the two surfaces apart.
   */
  RUNJS_OUTPUT_TYPE_MISMATCH: 'RUNJS_OUTPUT_TYPE_MISMATCH',

  // ---- Pass 2 / dormant integrations ----
  /**
   * `/api/scripts/generate` was called but no LLM provider is wired
   * up. Surfaces today on every Rynora deployment because Stage C is
   * deferred. The endpoint exists so contract tests catch shape drift
   * (`pass2-contract-tests`) before we replace the stub.
   */
  LLM_NOT_CONFIGURED: 'LLM_NOT_CONFIGURED',
  /**
   * `/api/scripts/execute` was called but no hosted Firecracker
   * sandbox is wired up. Surfaces today on every Rynora deployment
   * because Stage E is deferred.
   */
  SANDBOX_NOT_CONFIGURED: 'SANDBOX_NOT_CONFIGURED',
  /**
   * The user's prompt was rejected by `validateLLMPrompt` because it
   * looks like a prompt-injection attempt (override instructions,
   * exfil secrets, etc). M-R12 guard.
   */
  LLM_PROMPT_REJECTED: 'LLM_PROMPT_REJECTED',

  // ---- Cloud browser execution (Phase 5.5) ----
  /**
   * `executionMode='cloud'` was requested but the env's
   * `admin_config.cloudBrowser.enabled` is false (master kill-switch),
   * the Steel API key is empty, or the org's plan tier doesn't include
   * `executionLimits.cloudBrowser`.
   */
  CLOUD_BROWSER_NOT_CONFIGURED: 'CLOUD_BROWSER_NOT_CONFIGURED',
  /**
   * The org has consumed all their bundled cloud-browser minutes for
   * the calendar month AND hit the operator-tunable overage hard cap
   * (admin_config.cloudBrowser.overageHardCapMinutesPerOrg). Surfaces
   * before the run is dispatched so the customer is not billed for an
   * aborted session.
   */
  CLOUD_BROWSER_QUOTA_EXCEEDED: 'CLOUD_BROWSER_QUOTA_EXCEEDED',
  /**
   * The org already has the maximum allowed number of concurrent cloud
   * browser sessions running (per `executionLimits.maxConcurrentCloudSessions`).
   * Retry once an existing session releases.
   */
  CLOUD_BROWSER_CONCURRENCY_EXCEEDED: 'CLOUD_BROWSER_CONCURRENCY_EXCEEDED',

  /**
   * Agent execution queue depth for the org exceeded the operator ceiling
   * (`executionAgent.maxQueuedJobsPerOrg`). Job was not accepted — retry later.
   */
  EXECUTION_QUEUE_FULL: 'EXECUTION_QUEUE_FULL',
  /**
   * Org already has the maximum leased+running agent jobs
   * (`executionAgent.maxConcurrentJobsPerOrg`). New jobs may still queue
   * until depth limits; claim waits until capacity frees.
   */
  EXECUTION_ORG_CONCURRENCY_SATURATED: 'EXECUTION_ORG_CONCURRENCY_SATURATED',

  // ---- Cross-platform browser capability matrix (migration 053) ----
  /**
   * No enabled provider could satisfy the requested
   * `(engine, browser, os, ...)` tuple. The error `detail.reasons`
   * map lists every provider that was considered and why it was
   * rejected (host_os_mismatch / capability_not_supported / etc.) so
   * the picker UI can render a precise tooltip.
   */
  CAPABILITY_NOT_SUPPORTED: 'CAPABILITY_NOT_SUPPORTED',
  /**
   * The provider that COULD satisfy the requested capability is
   * disabled at the admin-config level (kill-switch, missing
   * credential, or the IMPLEMENTED_CLOUD_BROWSER_PROVIDERS allow-list).
   * Distinct from `CAPABILITY_NOT_SUPPORTED` so admins seeing the
   * error know it's a config flip rather than a fundamental
   * incompatibility.
   */
  CAPABILITY_PROVIDER_DISABLED: 'CAPABILITY_PROVIDER_DISABLED',
  /**
   * The requested capability needs a cloud provider but
   * `cloudBrowser.enabled = false` for this env. Tells the user
   * "this needs cloud and cloud is off" without dumping the full
   * per-provider rejection list.
   */
  CAPABILITY_REQUIRES_CLOUD: 'CAPABILITY_REQUIRES_CLOUD',
  /**
   * The requested capability falls outside the org's plan tier (e.g.
   * cross-browser SKU not entitled). Surfaces with an "Upgrade plan"
   * CTA in the picker.
   */
  CAPABILITY_REQUIRES_PLAN: 'CAPABILITY_REQUIRES_PLAN',

  // ---- Story → Tests authoring ----
  STORY_FETCH_FAILED: 'STORY_FETCH_FAILED',
  STORY_NOT_FOUND: 'STORY_NOT_FOUND',
  STORY_PARSE_FAILED: 'STORY_PARSE_FAILED',
  STORY_CONNECTOR_NOT_CONFIGURED: 'STORY_CONNECTOR_NOT_CONFIGURED',
  STORY_INGEST_UNSUPPORTED: 'STORY_INGEST_UNSUPPORTED',
  STORY_INGEST_TOO_LARGE: 'STORY_INGEST_TOO_LARGE',
  STORY_GENERATE_FAILED: 'STORY_GENERATE_FAILED',
  STORY_GENERATE_QUOTA: 'STORY_GENERATE_QUOTA',
  PREVIEW_RUN_QUOTA: 'PREVIEW_RUN_QUOTA',
  MANUAL_TEST_NOT_FOUND: 'MANUAL_TEST_NOT_FOUND',
  MANUAL_CYCLE_NOT_FOUND: 'MANUAL_CYCLE_NOT_FOUND',
  MANUAL_EXECUTION_NOT_FOUND: 'MANUAL_EXECUTION_NOT_FOUND',
  MANUAL_ATTACHMENT_TOO_LARGE: 'MANUAL_ATTACHMENT_TOO_LARGE',
  MANUAL_BUG_PROVIDER_NOT_CONNECTED: 'MANUAL_BUG_PROVIDER_NOT_CONNECTED',
  PDF_RENDERER_UNAVAILABLE: 'PDF_RENDERER_UNAVAILABLE',
  SHARE_LINK_NOT_FOUND: 'SHARE_LINK_NOT_FOUND',
  SHARE_LINK_DISABLED: 'SHARE_LINK_DISABLED',
  SHARE_LINK_EXPIRED: 'SHARE_LINK_EXPIRED',
  SHARE_LINK_REVOKED: 'SHARE_LINK_REVOKED',
  SHARE_LINK_RATE_LIMIT: 'SHARE_LINK_RATE_LIMIT',

  // ---- Contract testing (Mode A + Mode B) ----
  CONTRACT_CHECK_NOT_FOUND: 'CONTRACT_CHECK_NOT_FOUND',
  CONTRACT_CHECK_NAME_TAKEN: 'CONTRACT_CHECK_NAME_TAKEN',
  CONTRACT_CHECK_KIND_NOT_SUPPORTED: 'CONTRACT_CHECK_KIND_NOT_SUPPORTED',
  CONTRACT_CHECK_SPEC_UNREACHABLE: 'CONTRACT_CHECK_SPEC_UNREACHABLE',
  CONTRACT_CHECK_SPEC_INVALID: 'CONTRACT_CHECK_SPEC_INVALID',

  // ---- pending-gitlab-jenkins-depth (CI provider depth) ----
  /**
   * `--ci-provider=auto` was passed but no recognised provider env
   * fingerprint was found (no `GITHUB_ACTIONS`, no `GITLAB_CI`, no
   * `JENKINS_HOME`+`BUILD_NUMBER`). The CLI exits 0 with a stderr WARN
   * — provider-aware emit + status-post fan-out is skipped, the
   * baseline JSON / JUnit / console reporters still fire. Operators
   * can suppress the WARN by passing an explicit `--ci-provider`.
   */
  CI_PROVIDER_UNKNOWN: 'CI_PROVIDER_UNKNOWN',
  /**
   * `--ci-provider=gitlab` or `--ci-provider=jenkins` was passed (or
   * auto-detected) but no usable token / credential was found in the
   * environment. GitLab path looks for `GITLAB_TOKEN` then
   * `CI_JOB_TOKEN`. Jenkins path looks for `JENKINS_TOKEN` or
   * `JENKINS_USER`+`JENKINS_PASSWORD`. Status posters skip with a
   * stderr WARN — artifact emit still happens.
   */
  CI_TOKEN_MISSING: 'CI_TOKEN_MISSING',
  /**
   * The provider's REST API returned a non-2xx response (or the fetch
   * threw) when the CLI tried to post an MR comment / build
   * description. Best-effort — the exit code is NEVER flipped. We
   * record the HTTP status + truncated body on stderr and move on.
   */
  CI_STATUS_POST_FAILED: 'CI_STATUS_POST_FAILED',

  // ---- Annotation sets (defer-flowchart-server-persist) ----
  ANNOTATION_SET_NOT_FOUND: 'ANNOTATION_SET_NOT_FOUND',
  ANNOTATION_SET_INVALID_PAYLOAD: 'ANNOTATION_SET_INVALID_PAYLOAD',
  /**
   * `defer-annotation-collab` — optimistic concurrency conflict on
   * PUT. Returned with HTTP 409 when the request's
   * `expectedUpdatedAt` does not match the server's current
   * `updated_at`. The body includes the latest stored set + the
   * winning author so the editor can offer "discard / reload" UX.
   */
  ANNOTATION_SET_CONFLICT: 'ANNOTATION_SET_CONFLICT',

  // ---- Mobile emulation favourites (defer-mobile-favourites) ----
  MOBILE_FAVOURITE_NOT_FOUND: 'MOBILE_FAVOURITE_NOT_FOUND',
  MOBILE_FAVOURITE_LIMIT_REACHED: 'MOBILE_FAVOURITE_LIMIT_REACHED',
  MOBILE_FAVOURITE_INVALID_PAYLOAD: 'MOBILE_FAVOURITE_INVALID_PAYLOAD',

  // ---- defer-llm-org-byok ----
  /** Provider in the URL isn't one of openai / groq / anthropic. */
  ORG_LLM_PROVIDER_UNKNOWN: 'ORG_LLM_PROVIDER_UNKNOWN',
  /** Upsert body failed Zod validation (key length / model / baseUrl). */
  ORG_LLM_CREDENTIAL_INVALID_PAYLOAD: 'ORG_LLM_CREDENTIAL_INVALID_PAYLOAD',
  /** DELETE /llm-credentials/:provider but the org has no row for that provider. */
  ORG_LLM_CREDENTIAL_NOT_FOUND: 'ORG_LLM_CREDENTIAL_NOT_FOUND',

  // ---- defer-pickers-oauth-flow ----
  /** Admin has not yet enabled provider OAuth in admin-config. */
  INTEGRATION_OAUTH_DISABLED: 'INTEGRATION_OAUTH_DISABLED',
  /** Provider in the URL isn't one of gmail / outlook / imap. */
  INTEGRATION_PROVIDER_UNKNOWN: 'INTEGRATION_PROVIDER_UNKNOWN',
  /** Required admin-config credentials (client id / secret / redirect) are missing. */
  INTEGRATION_OAUTH_NOT_CONFIGURED: 'INTEGRATION_OAUTH_NOT_CONFIGURED',
  /** Provider returned a non-2xx response during code exchange. */
  INTEGRATION_OAUTH_FAILED: 'INTEGRATION_OAUTH_FAILED',
  /** State token in `complete` request did not match a pending row. */
  INTEGRATION_OAUTH_STATE_INVALID: 'INTEGRATION_OAUTH_STATE_INVALID',
  /** DELETE /integrations/connections/:id but no row matched (org, id). */
  INTEGRATION_CONNECTION_NOT_FOUND: 'INTEGRATION_CONNECTION_NOT_FOUND',

  // ---- defer-templates-user-saved + defer-templates-ai-generated ----
  /** POST /api/templates payload failed `UserFlowTemplatePayloadSchema`. */
  FLOW_TEMPLATE_INVALID_PAYLOAD: 'FLOW_TEMPLATE_INVALID_PAYLOAD',
  /** GET / PATCH / DELETE /api/templates/:id but no row matched (org, id). */
  FLOW_TEMPLATE_NOT_FOUND: 'FLOW_TEMPLATE_NOT_FOUND',
  /** Org has hit the configured per-org template ceiling. */
  FLOW_TEMPLATE_LIMIT_REACHED: 'FLOW_TEMPLATE_LIMIT_REACHED',
  /** AI generator disabled in admin-config or kill-switched. */
  FLOW_TEMPLATE_GENERATOR_DISABLED: 'FLOW_TEMPLATE_GENERATOR_DISABLED',
  /** AI provider returned a malformed `FlowTemplate` payload that can't be coerced. */
  FLOW_TEMPLATE_GENERATOR_INVALID_OUTPUT: 'FLOW_TEMPLATE_GENERATOR_INVALID_OUTPUT',

  // ---- defer-llm-tool-use ----
  /** POST /api/intent/tool-call rejected because admin disabled it. */
  TOOL_USE_DISABLED: 'TOOL_USE_DISABLED',
  /** Request body failed `LlmToolCallRequestSchema`. */
  TOOL_USE_INVALID_REQUEST: 'TOOL_USE_INVALID_REQUEST',
  /** Routed provider returned a non-2xx / unparseable response. */
  TOOL_USE_PROVIDER_FAILED: 'TOOL_USE_PROVIDER_FAILED',
  /** Routed provider doesn't support tool calling at all (e.g. mock). */
  TOOL_USE_PROVIDER_UNSUPPORTED: 'TOOL_USE_PROVIDER_UNSUPPORTED',

  // ---- Phase 8.20 Intent orphan soak ----
  /** Phase-5 Intent orphans disabled via `ai.intentPhase5OrphansEnabled`. */
  INTENT_PHASE5_ORPHANS_DISABLED: 'INTENT_PHASE5_ORPHANS_DISABLED',

  // ---- defer-debug-vision ----
  /** POST /api/intent/vision rejected because admin disabled it. */
  VISION_DISABLED: 'VISION_DISABLED',
  /** Request body failed `LlmVisionRequestSchema`. */
  VISION_INVALID_REQUEST: 'VISION_INVALID_REQUEST',
  /** Routed provider returned a non-2xx / unparseable response. */
  VISION_PROVIDER_FAILED: 'VISION_PROVIDER_FAILED',
  /** Routed provider doesn't support image inputs. */
  VISION_PROVIDER_UNSUPPORTED: 'VISION_PROVIDER_UNSUPPORTED',

  // ---- pending-test-observability-tier v2 ----
  /** POST /api/observability/fingerprints/:id/remediation rejected because admin disabled the surface. */
  OBSERVABILITY_AI_REMEDIATION_DISABLED: 'OBSERVABILITY_AI_REMEDIATION_DISABLED',
  /** Org exceeded the configured `aiRemediationDailyQuotaPerOrg`. */
  OBSERVABILITY_AI_REMEDIATION_QUOTA: 'OBSERVABILITY_AI_REMEDIATION_QUOTA',
  /** The configured AI provider didn't return a valid JSON remediation. */
  OBSERVABILITY_AI_REMEDIATION_FAILED: 'OBSERVABILITY_AI_REMEDIATION_FAILED',
  /** First-seen webhook URL is set but the outbound HTTP failed. */
  OBSERVABILITY_FIRST_SEEN_WEBHOOK_FAILED: 'OBSERVABILITY_FIRST_SEEN_WEBHOOK_FAILED',

  // ---- pending-prod-feedback-loop v0 ----
  /** Sentry connector consumed but no PAT / orgSlug / projectSlug / targetProjectId configured. */
  SENTRY_CONNECTOR_NOT_CONFIGURED: 'SENTRY_CONNECTOR_NOT_CONFIGURED',
  /** Sentry REST API returned non-2xx during the ingest poll. */
  SENTRY_CONNECTOR_FETCH_FAILED: 'SENTRY_CONNECTOR_FETCH_FAILED',

  // ---- pending-ai-exploratory-tester Session 1 ----
  /**
   * Operator hit `POST /api/agent/exploratory/run-once` for an org
   * whose effective `agent.exploratory.enabled` is `false`. The
   * route returns 409 + this code so the UI can render "enable the
   * exploratory pass first" rather than treating it as a silent
   * 200. Distinct from the wider `RATE_LIMITED` / `RUN_INVALID_STATE`
   * codes so dashboards can split "manual trigger blocked by kill
   * switch" out of the noise.
   */
  AGENT_EXPLORATORY_DISABLED: 'AGENT_EXPLORATORY_DISABLED',

  // ---- defer-templates-marketplace v0 ----
  /** GET / POST /api/marketplace/templates/:templateId — slug not in the curated bundle. */
  MARKETPLACE_TEMPLATE_NOT_FOUND: 'MARKETPLACE_TEMPLATE_NOT_FOUND',
  /** POST /api/marketplace/templates/:templateId/insert — body / projectId failed validation. */
  MARKETPLACE_INSERT_INVALID_PAYLOAD: 'MARKETPLACE_INSERT_INVALID_PAYLOAD',
  /** POST /api/marketplace/templates/:templateId/insert — projectId not visible to the caller's org. */
  MARKETPLACE_PROJECT_FORBIDDEN: 'MARKETPLACE_PROJECT_FORBIDDEN',

  // ---- Org home region / data-residency (Batch 3 PR #2 / mig 153) ----
  /**
   * Caller hit a tenant route on a Rynora cluster whose `currentRegion`
   * doesn't match the org's `home_region`. The response `detail`
   * surfaces the correct cluster's hostname so the frontend can
   * redirect. Distinct from `AUTH_INSUFFICIENT_SCOPE` because the
   * caller's credentials are fine — the request just landed in the
   * wrong cluster.
   */
  ORG_REGION_MISMATCH: 'ORG_REGION_MISMATCH',
  /**
   * `POST /api/orgs/:orgId/region` succeeded the schema + RBAC checks
   * but the migration worker is not yet operational (true for every
   * deployment in v0 — the worker lands in a follow-up PR). Returned
   * as 412 with `detail.expectedAvailability` set to the documented
   * ETA so the UI can render a useful "try again after X" message.
   */
  ORG_REGION_MIGRATION_UNAVAILABLE: 'ORG_REGION_MIGRATION_UNAVAILABLE',
  /**
   * Body's `toRegion` is one of the catalogue's `planned` regions
   * (no cluster yet). Distinct from `VALIDATION_INVALID_BODY` so the
   * UI can render "this region is on the roadmap — request access"
   * instead of a generic 400.
   */
  ORG_REGION_NOT_AVAILABLE: 'ORG_REGION_NOT_AVAILABLE',
  /**
   * Body's `confirmation` phrase didn't match the destination region's
   * display name (case-insensitive). Same UX pattern as the BYOK
   * disable confirmation.
   */
  ORG_REGION_CONFIRMATION_MISMATCH: 'ORG_REGION_CONFIRMATION_MISMATCH',
  /**
   * Body's `toRegion` is the same as the org's current `home_region`.
   * 409 because the request is structurally valid but semantically a
   * no-op.
   */
  ORG_REGION_SAME_AS_CURRENT: 'ORG_REGION_SAME_AS_CURRENT',

  // ---- Project knowledge (`pending-project-knowledge`) ----
  /** Operator kill switch `knowledge.enabled` is off. */
  KNOWLEDGE_DISABLED: 'KNOWLEDGE_DISABLED',
  /** Glossary would exceed `knowledge.glossaryMaxTerms` (default 50). */
  GLOSSARY_LIMIT: 'GLOSSARY_LIMIT',
  /** Journeys / personas / rules / constraints would exceed the per-project cap. */
  KNOWLEDGE_ENTRY_LIMIT: 'KNOWLEDGE_ENTRY_LIMIT',
  /** Entry body looked like a secret (password / OTP / API key). */
  KNOWLEDGE_PII_REJECTED: 'KNOWLEDGE_PII_REJECTED',

  // ---- Locator cache (`pending-locator-reliability` v0.2) ----
  /** No step locators and no `locator_cache` row for that intent. */
  LOCATOR_NOT_CACHED: 'LOCATOR_NOT_CACHED',
  /** Operator kill switch `noCode.locatorReliability.cache.enabled` is off. */
  LOCATOR_CACHE_DISABLED: 'LOCATOR_CACHE_DISABLED',

  // ---- Quality HQ (`pending-quality-hq`) ----
  /** Operator kill switch `qualityHq.enabled` is off. */
  QUALITY_HQ_DISABLED: 'QUALITY_HQ_DISABLED',
  /** `(project_id, slug)` already exists. */
  QUALITY_RISK_SLUG_TAKEN: 'QUALITY_RISK_SLUG_TAKEN',
  /** Risk id is not in this project. */
  QUALITY_RISK_NOT_FOUND: 'QUALITY_RISK_NOT_FOUND',
  SCENARIO_REVIEW_PENDING: 'SCENARIO_REVIEW_PENDING',
  SCENARIO_REVIEW_SELF_APPROVE: 'SCENARIO_REVIEW_SELF_APPROVE',
  SCENARIO_REVIEW_COMMENT_REQUIRED: 'SCENARIO_REVIEW_COMMENT_REQUIRED',
  /** Decide attempted after the scenario version changed since submit. */
  SCENARIO_REVIEW_STALE: 'SCENARIO_REVIEW_STALE',
  /** Actor is not permitted by the project review_policy reviewer lists. */
  SCENARIO_REVIEW_NOT_ALLOWED: 'SCENARIO_REVIEW_NOT_ALLOWED',

  // ---- QD-1 Prerequisite #2 — Required Scenario Policy ----
  /** Duplicate scenarioIds in a publish request or unique-constraint conflict. */
  REQUIRED_SCENARIO_POLICY_CONFLICT: 'REQUIRED_SCENARIO_POLICY_CONFLICT',
  /** Scenario id is not in this project (cross-project attach attempt). */
  REQUIRED_SCENARIO_POLICY_SCENARIO_FOREIGN: 'REQUIRED_SCENARIO_POLICY_SCENARIO_FOREIGN',
  /** Requested policy version does not exist for project+environment. */
  REQUIRED_SCENARIO_POLICY_VERSION_NOT_FOUND: 'REQUIRED_SCENARIO_POLICY_VERSION_NOT_FOUND',

  // ---- QD-2 — Decision snapshot / historical freeze ----
  /** No frozen snapshot exists for this build identity. */
  QD1_SNAPSHOT_NOT_FOUND: 'QD1_SNAPSHOT_NOT_FOUND',
  /** Freeze refused (incomplete identity / unresolved policy) — no partial snapshot. */
  QD1_FREEZE_REFUSED: 'QD1_FREEZE_REFUSED',

  // ---- QD-3 — FROZEN↔FROZEN decision diff ----
  /** Compare refused (cross-environment / incomplete identity / incompatible sides). */
  QD1_COMPARE_REFUSED: 'QD1_COMPARE_REFUSED',

  // ---- Current Build Identity (selection pointer; not a builds product) ----
  /** SET refused — identity not present in project test_runs population. */
  CURRENT_BUILD_IDENTITY_NOT_IN_POPULATION: 'CURRENT_BUILD_IDENTITY_NOT_IN_POPULATION',
  /** SET refused — incomplete buildId / commitSha / environment. */
  CURRENT_BUILD_IDENTITY_INCOMPLETE: 'CURRENT_BUILD_IDENTITY_INCOMPLETE',
} as const;

export type RynoraErrorCode = (typeof RynoraErrorCode)[keyof typeof RynoraErrorCode];

/**
 * The canonical error response shape. EVERY error response from the
 * Rynora API matches this shape (or extends with extra `detail` fields).
 *
 * Backend handlers should construct via the helper:
 *
 *   res.status(404).json(apiError(RynoraErrorCode.FLOW_NOT_FOUND));
 *
 * Frontend code should look up display copy via:
 *
 *   ERRORS[body.code]
 */
export const ApiErrorBody = z.object({
  code: z.string(),
  message: z.string(),
  detail: z.record(z.unknown()).optional(),
});

export type ApiErrorBody = z.infer<typeof ApiErrorBody>;

/**
 * Build a well-formed error body. The `message` argument is the
 * default English copy and is overridable per call-site (e.g. when
 * the backend wants to include a dynamic value the frontend can't
 * compute). Frontends should still prefer `ERRORS[code]` from
 * `@rynora/messages` over `body.message` for i18n flexibility.
 */
export function apiError(code: RynoraErrorCode, message: string, detail?: Record<string, unknown>): ApiErrorBody {
  return detail ? { code, message, detail } : { code, message };
}
